/**
 * activities-api.adapter — implementation ของ `ActivitiesApiPort`
 *
 * 🔀 **ไฟล์นี้มี 2 นโยบายที่ต้องไม่ปนกัน** (อัปเมื่อ Day 5 เพิ่ม endpoint ที่ต้อง login)
 *
 * | endpoint | auth | แนบอะไร |
 * |---|---|---|
 * | `GET /activities` · `GET /activities/:id` | `public` | **ไม่แนบอะไรเลย** |
 * | `POST /activities/:id/registrations` | `bearer` | `authorization` เท่านั้น |
 *
 * 🔓 **2 ตัวแรกห้ามแนบ cookie/token เด็ดขาด** — DoD Day 4: เปิดดูได้โดยไม่ต้อง login
 *    (พิสูจน์ด้วย api-test แล้ว) · แนบเข้าไป = เปิดช่องให้เผลอผูกกับ session
 *    แล้ววันหนึ่งลิงก์ที่แชร์ให้คนนอกจะเปิดไม่ได้ ซึ่งเป็นจุดประสงค์ทั้งหมดของการแชร์
 *
 * 🔴 **`getPublic` กับ `postRegistration` แยกฟังก์ชันโดยตั้งใจ ไม่ใช่ flag `withAuth`**
 *    flag จะทำให้ "แนบ token ไหม" กลายเป็นค่าที่ต้องส่งถูกทุกครั้ง ⇒ วันหนึ่งมีคนส่ง
 *    `true` ให้ `getActivity` แล้วลิงก์ที่แชร์ให้คนนอกจะเปิดไม่ได้ **เงียบ ๆ**
 *    (แยกฟังก์ชัน = เผลอไม่ได้ ต้องจงใจเรียกตัวที่แนบ token)
 *    ⚠️ `createHttpClient()` ของ `shared-web` **ไม่มี default ให้เลย** ด้วยเหตุผลเดียวกัน
 */
import {
  REGISTER_UNAUTHENTICATED,
  type ActivitiesApiPort,
  type ActivityDetail,
  type ActivitySummary,
  type RegisteredActivity,
} from '@theelite/frontend-member-feature-activities';
import { createHttpClient, type HttpResult } from '@theelite/frontend-shared-web';

export function createActivitiesApi(
  baseUrl: string,
  /**
   * อ่าน access token **ตอนเรียก ไม่ใช่ตอนประกอบ** — token เปลี่ยนได้ระหว่างอายุของแอป
   * (login/refresh/logout) · จับค่าไว้ตอนสร้าง adapter = ใช้ token ที่หมดอายุไปแล้ว
   */
  getAccessToken: () => string | null,
): ActivitiesApiPort {
  const http = createHttpClient(baseUrl);

  /** endpoint สาธารณะ — **ไม่มี headers ไม่มี credentials ไม่มี body** */
  const getPublic = async <T>(path: string): Promise<HttpResult<T>> =>
    http.request<T>({ method: 'GET', path, fallbackMessage: 'ระบบขัดข้องชั่วคราว' });

  /**
   * `POST /activities/:id/registrations` — **ตัวเดียวที่แนบ bearer** (Day 5)
   *
   * ⚠️ **ไม่ส่ง body** — endpoint ไม่รับ body (`memberId` มาจาก token) · และ **ต้องไม่ตั้ง
   *    `content-type: application/json`** ทิ้งไว้แบบไม่มี body เพราะ Fastify จะตอบ 400
   *    (`FST_ERR_CTP_EMPTY_JSON_BODY`) — backend รับมือไว้แล้วตั้งแต่ Day 5 แต่ฝั่งนี้
   *    ก็ไม่ควรพึ่งพาสิ่งนั้น: ไม่มี body = ไม่ต้องประกาศชนิดของ body
   */
  const postRegistration = async (
    activityId: string,
    accessToken: string | null,
  ): Promise<HttpResult<RegisteredActivity>> => {
    if (accessToken === null) {
      /**
       * ยังไม่ login — **ตอบเองโดยไม่ต้องยิง** · ยิงไปก็ได้ 401 อยู่ดี แต่ผู้ใช้ต้องรอ
       * round trip เปล่า ๆ บนเน็ตมือถือ · `reason` ต้องตรงกับที่ backend ใช้เป๊ะ
       * ⇒ ฟีเจอร์เห็นเหมือนกันทั้ง 2 ทาง ไม่ต้องมีโค้ดแยกเคส
       */
      return {
        ok: false,
        error: { reason: REGISTER_UNAUTHENTICATED, message: 'ต้องเข้าสู่ระบบก่อนจึงจะลงทะเบียนได้' },
      };
    }

    const result = await http.request<RegisteredActivity>({
      method: 'POST',
      path: `/activities/${encodeURIComponent(activityId)}/registrations`,
      headers: { authorization: `Bearer ${accessToken}` },
      fallbackMessage: 'ลงทะเบียนไม่สำเร็จ ลองอีกครั้ง',
    });

    /**
     * 🔴 **401 ทุกกรณี → `unauthenticated`** — backend ตอบ 401 ได้หลายทาง: binder
     *    ปฏิเสธ token (ไม่มี `error.reason` ในบอดี้) หรือ use-case ตอบ `member_not_found`
     *    ⇒ ทั้งหมดแปลว่า "ต้อง login ใหม่" สำหรับผู้ใช้ · รวมเป็นค่าเดียวที่นี่
     *    (ปล่อยผ่านไป = ฟีเจอร์ตกไปเป็น `failed` แล้วผู้ใช้เห็นปุ่มลองใหม่ที่ไม่มีวัน
     *     สำเร็จ แทนที่จะเห็นปุ่มเข้าสู่ระบบ)
     *
     * ⚠️ ตัดสินที่ **status ไม่ใช่ `reason`** โดยตั้งใจ — เทียบสตริง `http_401` จะพัง
     *    เงียบ ๆ วันที่ backend เริ่มส่ง `reason` ตัวใหม่มาพร้อม 401
     */
    if (!result.ok && result.error.status === 401) {
      return { ok: false, error: { ...result.error, reason: REGISTER_UNAUTHENTICATED } };
    }

    return result;
  };

  return {
    async listActivities() {
      /**
       * 🔴 backend ห่อด้วย `{ items }` ไม่ส่ง array เปล่าที่ระดับบนสุด (เพื่อเติม
       *    paging ทีหลังได้โดยไม่ทำให้ client เดิมพัง) — **แกะที่ชั้นนี้ที่เดียว**
       *    เพื่อให้ feature เห็นเป็น array ธรรมดา
       */
      const result = await getPublic<{ items: ActivitySummary[] }>('/activities');
      return result.ok ? { ok: true, activities: result.data.items } : result;
    },

    async getActivity(activityId) {
      const result = await getPublic<ActivityDetail>(
        `/activities/${encodeURIComponent(activityId)}`,
      );
      return result.ok ? { ok: true, activity: result.data } : result;
    },

    async registerForActivity(activityId) {
      const result = await postRegistration(activityId, getAccessToken());
      return result.ok ? { ok: true, registration: result.data } : result;
    },
  };
}
